import { AxiosResponse, AxiosError, type AxiosInstance } from 'axios';
import objectHash from 'object-hash';
// var objectHash = require('object-hash');
import {
  type GameResponseStructure,
  type RequestQueries,
  type ResponseStructure,
  type StructureQueries,
} from '../types/requests';
import { NuxtI18nInstance } from '@nuxtjs/i18n';
import { ConsentType, replaceUrlParams } from '../api/utils';
import { getKafkaData } from '../api/KafkaMapper';
import type {
  BillingHistoryResponse,
  InvoiceDataResponse,
  UserDataInfoResponse,
} from '../types/Response';

const defaultQueries: RequestQueries = {
  forceTimeout: 0,
  omitCache: false,
  preLive: true,
  minRating: 0,
};
const defaultRadisOptions = {
  url: process.env.NOMAD_ADDR_redis
    ? 'redis://' + process.env.NOMAD_ADDR_redis
    : 'redis://127.0.0.1:6379',
  disable: !process.env.NOMAD_ADDR_redis,
  expire: 60 * 60 * 1,
};

export default (
  axios: AxiosInstance,
  i18n: NuxtI18nInstance,
  backendUrl: string,
  auth: any,
  cacheFetch: any,
  store?: any,
  tenantObj?: any,
) => ({
  deviceId: null as number | string | null,
  operatingSystem: null as string | null,
  isDesktop: null as boolean | null,
  platformTypes: {},
  omitCache: false as boolean,
  os: null as string | null,
  async getStructure(
    name: string,
    query: StructureQueries = {},
    cancelToken?: AbortSignal | null,
    skipRoot?: boolean,
    headers = {},
  ): Promise<any> {
    (await this.getDeviceInfo()) as GameResponseStructure;
    defaultQueries.omitCache = this.omitCache;
    let maxRating =
      query?.maxRating ||
      store?.state?.parentalConfig?.maxRating ||
      store?.state?.tenant?.default?.maxRating ||
      tenantObj?.value?.default?.maxRating;
    // || auth?.ctx.store.state.parentalConfig?.maxRating;
    // if (
    //   auth?.ctx.store.state?.parentalConfig?.isActive &&
    //   (auth?.ctx.store.state.parentalConfig?.maxRating < maxRating || !maxRating)
    // ) {
    //   maxRating = auth?.ctx.store.state.parentalConfig.maxRating;
    // }
    const cQuery: StructureQueries = {
      ...defaultQueries,
      deviceId: this.deviceId,
      lastId: 0,
      ...query,
      lang: i18n.localeProperties.code,
      isDesktop: this.isDesktop,
      os: this.operatingSystem,
      // ...auth?.ctx.store.state.parentalConfig,
    };
    if (maxRating) {
      cQuery.maxRating = maxRating;
    }
    let res: AxiosResponse<ResponseStructure>;
    if (!cQuery.omitCache) {
      if (name === 'configurations') {
        delete cQuery.deviceId;
      }
      const tenant = axios.defaults.headers.common['X-Tenant-ID'];
      const hashKey = objectHash({
        tenant,
        name,
        cQuery,
        usertoken:
          axios.defaults.headers.common['Authorization'] ||
          axios.defaults.headers.common['authorization'],
      });
      res = await cacheFetch(
        {
          ...defaultRadisOptions,
          expire:
            name !== 'configurations' ? 60 * 2 : defaultRadisOptions.expire,
          key: `${tenant}-structure-${name}-${hashKey}`,
        },
        async () => {
          const returned = await axios.get(
            `${backendUrl}/structure/${name}?${buildQuery(cQuery)}`,
            { signal: cancelToken, headers },
          );

          return { data: returned.data };
        },
      );
    } else {
      res = await axios.get(
        `${backendUrl}/structure/${name}?${buildQuery(cQuery)}`,
        { signal: cancelToken, headers },
      );
    }
    if (res && res.data && res.data.substructure && !skipRoot) {
      this.getRoot(res.data, res.data.substructure);
    }
    return res && res.data && res.data.substructure;
  },
  async loadMissing(structures: ResponseStructure[] | any[]): Promise<void> {
    for (const structure of structures) {
      if (structure.promise) {
        const result: ResponseStructure = await this.getSubStructure(
          structure.root.name,
          structure.id,
          structure.root.data.params.query,
        );
        if (!result.promise) {
          this.getRoot(structure.root, result.substructure);
          Object.keys(result).forEach((i) => {
            structure[i] = result[i];
          });
          delete structure.promise;
          await this.loadMissing(structure.substructure);
        } else {
          structure.promise = result.promise;
          await this.loadMissing([structure]);
        }
      }
    }
  },
  getRoot(
    rootStructure: ResponseStructure | any,
    substructure: ResponseStructure,
  ): void {
    if (substructure && Array.isArray(substructure)) {
      substructure.forEach((item: ResponseStructure) => {
        item.root = rootStructure;
        this.getRoot(rootStructure, item.substructure);
        this.clickLinkWithQuerySupport(item, rootStructure.data.params.query);
        this.customPropsWithQuerySupport(item, rootStructure.data.params.query);
      });
    }
  },
  clickLinkWithQuerySupport(
    structure: ResponseStructure,
    query: StructureQueries,
  ): void {
    if (!structure || !structure.data || !structure.data.clickUrl) {
      return;
    }
    structure.data.clickUrl = replaceUrlParams(
      structure.data.clickUrl,
      query,
      auth,
    );
  },
  customPropsWithQuerySupport(
    structure: ResponseStructure,
    query: StructureQueries,
  ): void {
    if (!structure || !structure.data || !structure.data.customProperties) {
      return;
    }
    for (const key in structure.data.customProperties) {
      structure.data.customProperties[key] = replaceUrlParams(
        structure.data.customProperties[key] as string,
        query,
        auth,
      );
    }
  },
  async getSubStructure(
    name: string,
    id: string,
    query: StructureQueries,
    cancelToken?: AbortSignal,
  ): Promise<unknown> {
    await this.getDeviceInfo();
    defaultQueries.omitCache = this.omitCache;
    let maxRating =
      query?.maxRating ||
      store?.state?.parentalConfig?.maxRating ||
      query?.maxRating ||
      store?.state?.parentalConfig?.maxRating ||
      store?.state?.tenant?.default?.maxRating;
    //  || auth.ctx.store.state.parentalConfig?.maxRating;
    // if (
    //   auth.ctx.store.state?.parentalConfig?.isActive &&
    //   (auth.ctx.store.state.parentalConfig?.maxRating < maxRating || !maxRating)
    // ) {
    //   maxRating = auth.ctx.store.state.parentalConfig.maxRating;
    // }
    const cQuery: StructureQueries = {
      deviceId: this.deviceId,
      os: this.operatingSystem,
      ...defaultQueries,
      ...query,
      ...auth?.ctx?.store.state.parentalConfig,
    };
    if (maxRating) {
      cQuery.maxRating = maxRating;
    }
    let res: AxiosResponse<ResponseStructure> = await axios.get(
      `${backendUrl}/structure/${name}/fragment/${id}?${buildQuery(cQuery)}`,
      { signal: cancelToken },
    );
    return res && res.data;
  },
  async getMessageByLang(lang: string, tenantName?: string): Promise<any> {
    const headers: { [key: string]: string } = {};
    if (tenantName) {
      headers['X-Tenant-ID'] = tenantName;
    }
    const returned = await axios.get(`${backendUrl}/i18n/${lang}`, {
      headers: headers,
    });
    return { data: returned.data };
  },
  async getUserFavouritPlaylists(): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/like/playlists`,
      );
      return res && res.data;
    } catch (error) {
      console.error('Error In getting user favourite playlists');
    }
  },
  async getLeaderboard(contentId: any) {
    let data = {
      contentId: contentId,
      protocol: 'https',
    };
    let res: AxiosResponse<unknown> = await axios.post(
      `${backendUrl}/game/arcade-competition`,
      data,
    );
    return res && res.data;
  },

  async getMusicTrack(song: any, cancelToken: AbortSignal): Promise<unknown> {
    let data = {
      id: song.id,
      protocol: 'https',
      // ...getKafkaData(song, auth),
    };
    let res: AxiosResponse<unknown> = await axios.post(
      `${backendUrl}/song`,
      data,
      { signal: cancelToken },
    );
    return res && res.data;
  },
  async getVideo(video: any, protocol: string, drm?: string): Promise<unknown> {
    try {
      let data: any = {
        id: video.id,
        protocol, //‘HLSv3’ | ‘HLSv4’| ‘MPEG-DASH’ | ‘smooth’| ‘auto’;
        // ...getKafkaData(video, auth),
      };
      if (drm) {
        data.drm = drm;
      }
      if (video?.providerId) {
        data.externalProviderCode = video?.providerId;
      }
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/video/streaming`,
        data,
        {
          headers: {
            Pragma: 'no-cache',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
          },
        },
      );
      return res && res.data;
    } catch (err) {
      console.error('Error: getVideo');
    }
  },
  async getUserPlayHistory(articleId: number | string): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/user/history/PLAY/${articleId}`,
      );
      return res && res.data;
    } catch (err) {
      console.error('Error: getUserPlayHistory');
    }
  },
  async removeUserGameHistoryArticle(
    articleId: number | string,
  ): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.delete(
        `${backendUrl}/user/history/PLAY/${articleId}`,
      );
      return res && res.data;
    } catch (err) {
      throw err;
    }
  },

  async setUserPlayHistory(
    articleId: number | string,
    val: number | unknown,
    contentType: string,
  ): Promise<unknown> {
    try {
      let data = {
        userCurrentPlaying: val,
        contentType,
      };
      const res: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/user/history/PLAY/${articleId}`,
        data,
      );
      return res;
    } catch (err) {
      console.error('Error: setUserPlayHistory');
    }
  },

  async getLikedEntities(entityType: string): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/like/${entityType}`,
      );
      return res.data;
    } catch (err) {
      console.error('Error getLikedEntities : ');
    }
  },
  async like(entityType: string, entityId: string): Promise<unknown> {
    const body = {
      // ...getKafkaData({ id: entityId, contentType: entityType }, auth),
    };
    return (await axios.post(
      `${backendUrl}/like/${entityType}/${entityId}`,
      body,
    )) as AxiosResponse<unknown>;
  },
  async unlike(entityType: string, entityId: string): Promise<unknown> {
    const body = {
      // ...getKafkaData({ id: entityId, contentType: entityType }, auth),
    };
    return (await axios.delete(`${backendUrl}/like/${entityType}/${entityId}`, {
      data: body,
    })) as AxiosResponse<unknown>;
  },
  async voteUp(entityType: string, entityId: string): Promise<unknown> {
    try {
      const response = await axios.put(
        `${backendUrl}/like/vote/up/${entityType}/${entityId}`,
      );

      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },
  async voteDown(entityType: string, entityId: string): Promise<unknown> {
    try {
      const response = await axios.put(
        `${backendUrl}/like/vote/down/${entityType}/${entityId}`,
      );

      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },
  async deleteVote(entityType: string, entityId: string): Promise<unknown> {
    try {
      const response = await axios.delete(
        `${backendUrl}/like/vote/${entityType}/${entityId}`,
      );

      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },
  async getArticleOneTimePurchase(articleId: string) {
    let url = `${backendUrl}/purchase/ArticleOneTimePurchase`;
    try {
      return await axios.get(url, {
        params: {
          articleId,
        },
      });
    } catch (err) {
      throw err.response;
    }
  },
  async verifyOneTimePurchasePin(
    uuid: string,
    articleId: string,
    pin: string,
    purchaseToken: string,
  ) {
    let url = `${backendUrl}/purchase/VerifyOneTimePurchasePin`;
    try {
      return await axios.post(url, {
        paymentTransactionUuid: uuid,
        articleId,
        pin,
        purchaseToken,
      });
    } catch (err) {
      // console.info(err, 'verifyOneTimePurchasePin');
    }
  },
  async getArticlePurchaseOptions(
    articleId: string | number,
    isDTO?: boolean,
    contentType?: string,
    externalProviderCode?: number | string,
    referralSource?: string,
  ) {
    try {
      let url = `${backendUrl}/purchase/ArticlePurchaseOptions/${articleId}`;
      await this.getDeviceInfo();
      const extraHeaders = referralSource
        ? { 'x-source-referer': referralSource }
        : {};

      const res = await axios.get(url, {
        headers: extraHeaders,
        params: {
          isDTO: isDTO,
          contentType,
          deviceId: this.deviceId,
          externalProviderCode,
        },
      });

      return res;
    } catch (error) {
      return error;
    }
  },
  async getSubscriptionTypes(): Promise<unknown> {
    const tenant = axios.defaults.headers.common['X-Tenant-ID'];
    const hashKey = objectHash({
      name: 'getSubscriptionTypes',
    });
    let res = await cacheFetch(
      {
        ...defaultRadisOptions,
        key: `${tenant}-subscription-types-${hashKey}`,
        expire: 60 * 60 * 2,
      },
      async () => {
        const requset = await axios.get(`${backendUrl}/subscription/type`);
        return { data: requset.data };
      },
    );
    return res;
  },
  async getUserSubscription(inclCancelled: boolean = true): Promise<unknown> {
    try {
      return (await axios.get(
        `${backendUrl}/subscription?inclCancelled=${inclCancelled}&inclSubscriptionType=true`,
      )) as AxiosResponse<unknown>;
    } catch (error) {
      throw error;
    }
  },
  async isVoucherbookEligible(subsTypeId: string = ''): Promise<unknown> {
    try {
      return (await axios.get(
        `${backendUrl}/subscription/isVoucherbookEligible?subsTypeId=${subsTypeId}`,
      )) as AxiosResponse<boolean>;
    } catch (error) {
      throw error;
    }
  },
  async subscribeUser(
    typeID: number,
    lang: string,
    redirectUrl: string,
    price: string,
    userId: string,
    subscriptionConsentList: any,
    migrateSubId: number,
    selectedOptions: any,
    upgradeTo: any,
    selectedUserCard: any = null,
    samsungpay: any = null,
  ): Promise<unknown> {
    let marketingCampaign = sessionStorage.getItem('marketingCampaign');
    if (marketingCampaign) {
      try {
        marketingCampaign = JSON.parse(marketingCampaign);
      } catch (error) {
        console.error('error when parsing campgain queries');
      }
    }
    const bodyData = {
      subscriptionTypeId: typeID,
      lang: lang?.value || lang,
      redirect: redirectUrl,
      userId: parseInt(userId),
      subscriptionConsentList: subscriptionConsentList,
      migrateSubId,
      selectedOptions,
      upgradeTo,
      marketingCampaign,
      cardId: selectedUserCard?.length ? selectedUserCard : null,
      samsungpay,
      // ...getKafkaData({ price, typeID }, auth),
    };
    const res: AxiosResponse<unknown> = await axios
      .post(`${backendUrl}/subscription`, bodyData)
      .catch((err) => {
        if (
          err?.response?.data?.data?.error ===
            'REQUIRED_IDENTITY_FIELD_NOT_FOUND' &&
          err?.response?.data?.status === 404
        ) {
          return err.response;
        } else {
          // console.info(err, 'add subscription');
          throw err;
        }
      });
    return res;
  },
  async cancelSubscription(
    subscriptionId: string,
    lang: string,
    redirectionUrl: string,
  ): Promise<unknown> {
    try {
      const separator = redirectionUrl.includes('?') ? '&' : '?';
      const cancelQuery = `${separator}cancellationFlow=true`;
      redirectionUrl = `${redirectionUrl}${cancelQuery}`;
      const queries = { lang: lang?.value || lang, redirectionUrl };
      const res = (await axios.delete(
        `${backendUrl}/subscription/${subscriptionId}?${buildQuery(queries)}`,
      )) as AxiosResponse<unknown>;
      return res;
    } catch (error) {
      // console.info('cancelled error', error);
      throw error;
    }
  },
  async getUserInfo(): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/user/profile`,
      );
      return res.data;
    } catch (err) {
      // console.info('Error while getting user info : ', err);
    }
  },
  async updateUserInfo({
    name,
    lastName,
    email,
    userInformation,
  }): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/user/profile`,
        { name, lastName, email, userInformation },
      );
      return res;
    } catch (err) {
      console.error('Error while updating user info : ');
      throw err;
    }
  },
  async deleteUserAccount(
    redirectionUrl: string,
    lang: string,
  ): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/user/profile`,
        { redirectionUrl, lang },
      );

      return res;
    } catch (err) {
      console.error('Error while deleting user account');
      throw err;
    }
  },
  async generateSongDownloadLink(item): Promise<unknown> {
    try {
      await this.getDeviceInfo();
      let data = {
        articleId: item.id,
        distributionChannel: 'WEB',
        deviceId: this.deviceId,
        collectionId: item?.collectionInfo?.articleId,
        // ...getKafkaData(item, auth),
      };

      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/song/download`,
        data,
      );

      return res.data;
    } catch (error) {
      // console.info('Error: Download Song', error);
      throw error;
    }
  },
  async getWallet(limit = 50, offset = 0): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/wallet/userarticles?inclContTypes=FULL_TRACK_MUSIC&inclContTypes=AUDIO_BOOK&devId=-1&limit=${limit}&offset=${offset}`,
      );
      return res.data;
    } catch (err) {
      console.error('Error Get Wallet : ');
    }
  },
  async isFreeTrialEligible(): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/subscription/isFreeTrialEligible`,
      );
      return res.data;
    } catch (err) {
      throw err;
    }
  },
  async subscribeToFreeTrial(
    redirectUrl: string,
    lang: string,
  ): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/subscription/subscribeToFreeTrial`,
        {
          redirect: redirectUrl,
          lang,
        },
      );
      return res.data;
    } catch (err) {
      console.error('Error post subscribeToFreeTrial : ');
      throw err;
    }
  },
  async getUserWallet(articleId: string): Promise<unknown> {
    try {
      return (await axios.get(
        `${backendUrl}/wallet/walletarticle/article/${articleId}`,
      )) as AxiosResponse<unknown>;
    } catch (error) {}
  },
  async getUserRating(articleId: string): Promise<unknown> {
    try {
      return (await axios.get(
        `${backendUrl}/rating/user/article/${articleId}`,
      )) as AxiosResponse<unknown>;
    } catch (error) {}
  },
  async putUserRating(
    articleId: string,
    score: string,
    article: any,
    contentType?: string,
  ): Promise<unknown> {
    await this.getDeviceInfo();
    let reqBody = {
      articleId: articleId,
      deviceId: this.deviceId,
      score: score,
      contentType: contentType ? contentType : '',
      // ...getKafkaData(article, auth),
    };
    try {
      return (await axios.put(
        `${backendUrl}/rating`,
        reqBody,
      )) as AxiosResponse<unknown>;
    } catch (error) {
      // console.info('Add user rating error', error);
    }
  },
  async getCategoryArtwork(categoryId: string): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/content/category/artwork`,
        {
          params: { categoryId: categoryId },
        },
      );
      return res && res.data && res.data;
    } catch (error) {
      return error.response;
    }
  },
  async getDeviceInfo(): Promise<GameResponseStructure | void> {
    if (this.deviceId) {
      return;
    }
    if (store.state?.device) {
      this.deviceId = store.state.device.deviceId;
      this.isDesktop = store.state.device.isDesktop;
      this.operatingSystem = store.state.device.operatingSystem;
      this.platformTypes = store.state.device.platformTypes;
      return;
    }
    try {
      const userAgent = axios.defaults.headers.common['user-agent'];
      const hashKey = objectHash({
        userAgent,
      });
      let res = await cacheFetch(
        {
          ...defaultRadisOptions,
          key: `device-${hashKey}`,
          expire: 60 * 60 * 24,
        },
        async () => {
          let headers = useRequestHeaders();
          const userAgent = headers['x-user-agent'] || headers['user-agent'];
          let value = await useAsyncData(
            'deviceId',
            async () => {
              const res: AxiosResponse<GameResponseStructure> = await axios.get(
                `${backendUrl}/device`,
                {
                  headers: {
                    'user-agent': userAgent,
                  },
                },
              );
              return res.data;
            },
            {
              deep: false,
            },
          );

          return { data: value.data.value };
        },
      );
      this.isDesktop = res.data?.deviceAttributes?.desktop as boolean;
      const {
        tablet,
        desktop,
        modernPhone: mobile,
      } = res.data?.deviceAttributes;
      this.platformTypes = { tablet, desktop, mobile };
      const os = res.data?.deviceAttributes?.operatingSystem as string;
      if (os) {
        this.operatingSystem = os;
      } else {
        if (this.isDesktop) {
          this.operatingSystem = 'DESKTOP';
        }
      }
      this.deviceId = res?.data?.id as number | string;
      store.commit('setDevice', {
        isDesktop: this.isDesktop,
        deviceId: this.deviceId,
        operatingSystem: this.operatingSystem,
        platformTypes: this.platformTypes,
      });
    } catch (error: any) {
      console.warn('error get device id', error?.config, error?.response);
      this.deviceId = 1; //fall back
    }
  },
  async getOnlineGameUrl(
    gameId: string,
    referralSource?: string,
  ): Promise<string | void> {
    try {
      await this.getDeviceInfo();
      const extraHeaders = referralSource
        ? { 'x-source-referer': referralSource }
        : {};
      let body = {
        ...buildGameData(gameId),
        deviceId: this.deviceId as number | string,
      };
      let res: AxiosResponse<GameResponseStructure> = await axios.post(
        `${backendUrl}/game/play`,
        body,
        { headers: extraHeaders },
      );
      return res.data.uri;
    } catch (err) {
      // console.info(err, 'error get game online url');
    }
  },
  async getOnlineGameFreeUrl(
    gameId: string,
    referralSource?: string,
  ): Promise<string | void> {
    try {
      await this.getDeviceInfo();
      const extraHeaders = referralSource
        ? { 'x-source-referer': referralSource }
        : {};
      let body = {
        ...buildGameData(gameId),
        deviceId: this.deviceId as number | string,
      };
      let res: AxiosResponse<any> = await axios.post(
        `${backendUrl}/game/play-free`,
        body,
        { headers: extraHeaders },
      );
      return res.data.url;
    } catch (err) {
      throw err.response;
    }
  },
  async getDownloadableGameUrl(
    game: any,
    referralSource?: string,
  ): Promise<GameResponseStructure> {
    try {
      await this.getDeviceInfo();
      const extraHeaders = referralSource
        ? { 'x-source-referer': referralSource }
        : {};
      let body = {
        ...buildGameData(game.id),
        // ...getKafkaData(game, auth),
        deviceId: this.deviceId as number | string,
      };
      let res: AxiosResponse<GameResponseStructure> = await axios.post(
        `${backendUrl}/game/download`,
        body,
        { headers: extraHeaders },
      );
      return { ...res.data, operatingSystem: this.operatingSystem };
    } catch (err: any) {
      // console.info(err.response, 'error get game downloadable url');
      return { uri: undefined, operatingSystem: null } as GameResponseStructure;
    }
  },
  async reportTracking(
    ticketID: string,
    type: string,
    duration: number,
    maxDuration: number,
    isEnd: boolean,
    date: Date,
  ): Promise<unknown> {
    let body = {
      ticketId: ticketID,
      type: type,
      date: date,
      usageDuration: duration,
      maxUsageDuration: maxDuration,
      offline: false,
      partial: isEnd,
    };
    let response: AxiosResponse<unknown> = await axios.post(
      `${backendUrl}/content/streaming/report`,
      body,
    );
    return response;
  },
  async addUserHistory(
    articleId: number,
    action: string,
    contentType: string,
  ): Promise<any> {
    try {
      const data = {
        contentType,
      };
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/user/history/${action}/${articleId}`,
        data,
      );
      return response;
    } catch (err) {
      // console.info('Add user history', err);
    }
  },
  async consumSubscriptionVoucher(code: string, typeId?: number): Promise<any> {
    try {
      const data = {
        voucherCode: code,
      };
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/voucher/consumption/subscription`,
        data,
      );
      return response.data;
    } catch (err) {
      return err.response;
    }
  },

  async consumeVoucher(
    externalProviderCode: string,
    voucherId: string,
    quantity: number,
  ): Promise<any> {
    try {
      const data = {
        externalProviderCode: externalProviderCode,
        voucherRequest: {
          voucherId: voucherId,
          quantity: quantity,
        },
      };
      const response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/voucher/consumption/voucher`,
        data,
      );
      return response.data;
    } catch (err) {
      return err.response;
    }
  },
  async resendVoucher(voucherId: string): Promise<any> {
    try {
      const data = {
        voucherRequest: {
          voucherEntityId: voucherId,
        },
      };

      const response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/voucher/resend/voucher`,
        data.voucherRequest,
      );
      return response.data;
    } catch (err) {
      throw err;
    }
  },
  async getOneTimePaymentOptions(
    redirectionUrl: string,
    lang: string,
    transactionId: number,
  ): Promise<any> {
    try {
      const data = {
        redirectionUrl: redirectionUrl,
        lang: lang,
        transactionId: transactionId,
      };

      const response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/subscription/oneTimePaymentOptions`,
        data,
      );
      return response.data;
    } catch (err) {
      return err.response;
    }
  },
  async updatePartnerMetadataSubscriptionAttribute(
    subscriptionId: string,
    value: any,
  ): Promise<any> {
    try {
      const data = {
        value,
      };

      const response: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/subscription/${subscriptionId}/partnerMetadata/attributes`,
        data,
      );
      return response.data;
    } catch (err) {
      return err.response;
    }
  },
  async oneTimePurchase(
    paymentOptionUUID: string,
    sessionName: string,
    redirectionUrl: string,
  ): Promise<any> {
    try {
      const data = {
        paymentOptionUUID: paymentOptionUUID,
        sessionName: sessionName,
        redirectionUrl: redirectionUrl,
      };
      const response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/subscription/oneTimePurchase`,
        data,
      );
      return response.data;
    } catch (err) {
      return err.response;
    }
  },

  async createParntalControlAcount(data) {
    try {
      let response: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/parental-control`,
        data,
      );
      return response;
    } catch (err) {
      return err.response;
    }
  },
  async getParntalControlAcount() {
    try {
      let response: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/parental-control`,
      );
      return response.data;
    } catch (err) {
      return err.response;
    }
  },
  async getUserCards() {
    try {
      let response: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/creditCard/creditCardInfo`,
      );
      return response.data;
    } catch (err: any) {
      return err.response;
    }
  },
  async addNewUserCard(body: any) {
    try {
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/creditCard/session/add/card`,
        body,
      );
      return response.data;
    } catch (err: any) {
      return err.response;
    }
  },
  async setUserCardAsDefault(cardId: string, body: any) {
    try {
      let response: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/creditCard/creditCardInfo/${cardId}`,
        body,
      );
      return response.data;
    } catch (err: any) {
      return err.response;
    }
  },
  async removeUserCard(cardId: string) {
    try {
      let response: AxiosResponse<unknown> = await axios.delete(
        `${backendUrl}/creditCard/creditCardInfo/${cardId}`,
      );
      return response.data;
    } catch (err: any) {
      return err.response;
    }
  },
  async verifyParentalControl(pass: any) {
    try {
      const body = {
        password: pass,
      };
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/parental-control/verify/parentalpassword`,
        body,
      );
      return response.data;
    } catch (err) {
      throw err.response;
    }
  },
  async sendEmailAndGenerateUrl(sendGridConfig: any) {
    try {
      const body = {
        sendGridConfig: sendGridConfig,
      };
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/parental-control/sendEmail`,
        body,
      );
      return response;
    } catch (err) {
      throw err?.response?.data;
    }
  },
  async resetParentalPasscode(randomCode: any) {
    try {
      const body = {
        randomCode,
      };
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/parental-control/resetPasscode`,
        body,
      );
      return response.data;
    } catch (err) {
      throw err.response;
    }
  },
  async getUserPurchaseOptions(articleId, contentType?, deviceId?) {
    try {
      let response: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/purchase/user/purchaseOptions`,
        {
          params: { articleId, contentType, deviceId },
        },
      );
      return response.data;
    } catch (err) {
      return err.response;
    }
  },
  async markTournamentInvitationToBeNotified(
    provider: string,
    teamId?: string,
  ): Promise<any> {
    try {
      const data = {
        provider,
        ...(teamId && { teamId }),
      };
      const response: AxiosResponse<unknown> = await axios.patch(
        `${backendUrl}/tournament/markInvitationNotified?${buildQuery(data)}`,
      );
      return response.data;
    } catch (err) {
      throw err;
    }
  },
  async sendJoinTournamentTeamRequest(
    userInputs: any,
    provider: string,
  ): Promise<any> {
    const joinRequestData = {
      userName: userInputs.username,
      ingameId: userInputs.gameId,
      provider: provider,
      // ...getKafkaData({ userInputs }, auth),
    };
    try {
      const response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/tournament/sendJoinRequest/${userInputs.teamId}`,
        joinRequestData,
      );
      return response.data;
    } catch (err) {
      throw err.response;
    }
  },
  async getLogoutUrl(redirectUrl: string, lang: string) {
    try {
      const body = {
        redirectUrl,
        lang,
      };
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/logout`,
        body,
      );
      return response;
    } catch (err) {
      throw err?.response;
    }
  },
  async registerTournament(
    userInputs: any,
    provider: string,
    tournamentId: string,
  ): Promise<any> {
    const teamData = {
      userName: userInputs.username,
      ingameId: userInputs.gameId,
      teamName: userInputs.teamName || undefined,
      provider: provider,
    };
    try {
      const response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/tournament/registerTournament/${tournamentId}`,
        teamData,
      );
      return response;
    } catch (err) {
      throw err.response;
    }
  },

  async addInvitationResponse(
    teamId: string,
    provider: string,
    status: string,
    userId: string,
  ): Promise<unknown> {
    try {
      let data = {
        provider,
        status,
        userId,
      };
      return (await axios.patch(
        `${backendUrl}/tournament/addInvitationResponse/${teamId}`,
        data,
      )) as AxiosResponse<unknown>;
    } catch (err) {
      throw err;
    }
  },

  async selectUserTheme(alternatTheme) {
    try {
      let response: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/user/selectUserTheme`,
        { alternatTheme },
      );
      return response;
    } catch (err) {
      return err?.response;
    }
  },
  async checkUserTheme() {
    try {
      let response: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/user/checkUserTheme`,
      );
      return response?.data;
    } catch (err) {
      return err?.response;
    }
  },
  async selectUserLocale(lang) {
    try {
      let response: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/user/selectUserLang`,
        { selectedLang: lang },
      );
      return response;
    } catch (err) {
      return err?.response;
    }
  },
  async checkUserLocale() {
    try {
      let response: AxiosResponse<unknown> = await axios.get(
        `${backendUrl}/user/checkUserLang`,
      );
      return response?.data;
    } catch (err) {
      return err?.response;
    }
  },
  async sendEventToKafka(item, eventType): Promise<unknown> {
    let body: any = {
      // ...getKafkaData(item, auth),
    };
    try {
      let res = (await axios.post(
        `${backendUrl}/kafka/event/${eventType}`,
        body,
      )) as AxiosResponse<unknown>;
      return res;
    } catch (err) {
      return err?.response;
    }
  },
  async ValidateSubscriptionWithoutConsumption(
    externalProviderCode: number | string,
  ) {
    try {
      const body = {
        externalProviderCode: externalProviderCode,
      };
      let response: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/subscription/validate/external`,
        body,
      );
      return response;
    } catch (err) {
      return err?.response;
    }
  },
  async subscriptionApiFlow(
    paymentOption: any,
    body?: Object,
  ): Promise<unknown> {
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/subscription/api`,
        {
          paymentOptionUUID: paymentOption.identifier,
          body,
        },
      );
      return res.data || res;
    } catch (err) {
      console.error('Error post subscriptionApiFlow : ');
      throw err;
    }
  },
  async apiFlow(opts: {
    paymentOption: any;
    type: string;
    body?: any;
  }): Promise<unknown> {
    const { type, paymentOption, body } = opts;
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/subscription/api/${type}`,
        {
          paymentOptionUUID: paymentOption.identifier,
          redirectionUrl: paymentOption.redirectionUrl,
          body,
        },
      );
      return res.data;
    } catch (err) {
      console.error('Error post subscriptionApiFlow : ');
      throw err;
    }
  },
  async startChallenge(challengeId: string): Promise<unknown> {
    try {
      return (await axios.patch(
        `${backendUrl}/kafka/optInChallenge/${challengeId}`,
        {},
      )) as AxiosResponse<unknown>;
    } catch (err) {
      throw err;
    }
  },
  async getPartnerUrl(
    subTypeIds,
    user,
    partnerName,
    externalProviderCode,
    partnerProductCode,
  ): Promise<unknown> {
    const userId = user.userId;
    const userUUID = user.UUID;
    const bodyData = {
      userId: parseInt(userId),
      userUuid: userUUID,
      subTypeIds,
      partnerName,
      externalProviderCode,
      partnerProductCode,
    };
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/partner/getRedirectionUrl`,
        bodyData,
      );
      if (res instanceof AxiosError) {
        throw res;
      }
      return res?.data;
    } catch (err) {
      throw err;
    }
  },
  async getPartnerLoginOptions(
    redirectUrl,
    lang,
    tenantName,
  ): Promise<unknown> {
    const bodyData = {
      redirectUrl,
      lang,
      tenantName,
    };
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/partner/login/${tenantName}`,
        bodyData,
      );
      return res?.data;
    } catch (err) {
      throw err;
    }
  },

  async getPartnerSignupOptions(redirectionUrl?, lang?): Promise<unknown> {
    redirectionUrl = redirectionUrl || `${location.origin}${location.href}`;
    lang = lang || 'en';
    const bodyData = {
      redirectionUrl,
      lang,
    };
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/monsooq/mss-login/api/session/signup`,
        bodyData,
      );
      return res?.data;
    } catch (err) {
      throw err;
    }
  },

  async getD2CSignupOptions(tenantName, redirectUrl?, lang?): Promise<unknown> {
    redirectUrl = redirectUrl || `${location.origin}${location.href}`;
    lang = lang || 'en';
    const bodyData = {
      redirectUrl,
      tenantName,
      lang,
    };
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/partner/signup`,
        bodyData,
      );
      return res?.data;
    } catch (err) {
      throw err;
    }
  },

  async mssFlowWithSessionAndIdentifier(
    session: string,
    identifier: string,
    bodyData: {} = {},
  ) {
    if (!session || !identifier) {
      return;
    }
    try {
      let requestUrl = `${backendUrl}/monsooq/mss-login/api/${session}/${identifier}`;
      let res: AxiosResponse<unknown> = await axios.post(requestUrl, bodyData);
      return res?.data;
    } catch (error: any) {
      throw error;
    }
  },

  async getPartnerRedirectionInfo(
    externalProviderCode: string | string[],
  ): Promise<unknown> {
    const bodyData = {
      externalProviderCode,
    };
    try {
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/partner/getRedirectionUrl`,
        bodyData,
      );
      if (res.status == 402) {
        return res?.response?.data;
      }
      return res?.data;
    } catch (err) {
      if (err?.response?.status === 402) {
        return err?.response?.data;
      }
      throw err;
    }
  },
  async getEnabledSubscriptionTypeById(
    subscriptionId: string | number,
  ): Promise<unknown> {
    try {
      let res = (await axios.get(
        `${backendUrl}/subscription/enabled/${subscriptionId}`,
      )) as AxiosResponse<unknown>;
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  async postLoginApiFlow(data: any): Promise<unknown> {
    try {
      const res = (await axios.post(
        `${backendUrl}/login/api`,
        data,
      )) as AxiosResponse<unknown>;
      if (res?.data) {
        return res?.data;
      }
      if (res?.status !== 200 && res?.status !== 202 && res?.status !== 201) {
        throw res;
      }
    } catch (error) {
      throw error;
    }
  },
  async postSubscribeMaterna(data: {
    url: string;
    [key: string]: any;
  }): Promise<unknown> {
    try {
      const res = (await axios.post(
        `${backendUrl}/info-service/subscribe`,
        data,
      )) as AxiosResponse<unknown>;

      if (res?.data) {
        return res.data;
      }

      if (![200, 201, 202].includes(res.status)) {
        throw res;
      }
    } catch (error) {
      throw error;
    }
  },
  async getSubscribedServices(): Promise<unknown> {
    try {
      const res = (await axios.get(
        `${backendUrl}/info-service/subscriptions`,
      )) as AxiosResponse<unknown>;
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  async getDailySubscribedServices(): Promise<unknown> {
    try {
      const res = (await axios.get(
        `${backendUrl}/info-service/subscriptions/daily`,
      )) as AxiosResponse<unknown>;
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  async getDailyAboSubscribedServices(): Promise<unknown> {
    try {
      const res = (await axios.get(
        `${backendUrl}/info-service/subscriptions/daily-abo`,
      )) as AxiosResponse<unknown>;
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  async getAddArticleCount(
    articleId: string | number,
    category: string,
  ): Promise<unknown> {
    try {
      const res = (await axios.get(
        `${backendUrl}/concon/countread?id=${articleId}&category=${category}`,
      )) as AxiosResponse<unknown>;
      return res.data;
    } catch (error) {
      throw error;
    }
  },
  async generateDeliveryLink(item): Promise<unknown> {
    try {
      await this.getDeviceInfo();
      let data = {
        articleId: item.id,
        deviceId: this.deviceId,
      };
      let res: AxiosResponse<unknown> = await axios.post(
        `${backendUrl}/delivery/article/legacy`,
        data,
      );
      return res.data;
    } catch (error) {
      // console.info('Error: Delivery Url', error);
      throw error;
    }
  },
  async updateUserConsent(
    type: ConsentType,
    body: { version: number; enableKeys: string[] },
  ) {
    try {
      let response: AxiosResponse<unknown> = await axios.put(
        `${backendUrl}/user/consent?type=${type}`,
        body,
      );
      return response;
    } catch (err) {
      return err?.response;
    }
  },
  async getBillingHistory({
    size = 10,
    page = 1,
  }: {
    size: number;
    page: number;
  }): Promise<AxiosResponse<BillingHistoryResponse>> {
    try {
      const res = await axios.get(
        `${backendUrl}/purchase/billing/history?size=${size}&page=${page}`,
      );
      return res;
    } catch (error) {
      throw error;
    }
  },
  async getInvoiceData(
    paymentTransactionUuid: string,
  ): Promise<InvoiceDataResponse> {
    try {
      const res = await axios.get(
        `${backendUrl}/purchase/billing/history/invoice?paymentTransactionUuid=${paymentTransactionUuid}`,
      );
      return res.data;
    } catch (error) {
      throw error;
    }
  },
});

function buildQuery(obj: StructureQueries | any): string {
  let str = '';
  for (const key in obj) {
    if (str != '') {
      str += '&';
    }
    str += key + '=' + encodeURIComponent(obj[key]);
  }
  return str;
}

function buildGameData(gameId: number | string): typeof bodyData {
  const bodyData = {
    articleId: gameId,
    distributionChannel: 'WEB',
  };
  return bodyData;
}
